import { create } from 'zustand'

/**
 * The web tab's "update required" state (AUTHZ-02 E2, decision 3).
 *
 * Entered when a tab sees a deployed `version.json` with `mandatory: true` that is not the build
 * it runs. IRREVOCABLE for the life of the page: there is no action that leaves it, only the
 * reload that replaces the page. While it holds, the axios interceptor refuses new server work
 * (api-client.ts), queued writes still drain, and WebUpdateRequiredNotice reloads once the queue
 * has drained and a short grace period for an unsent draft has passed.
 *
 * Not persisted: the next page load decides afresh from `version.json`.
 */
type WebUpdateState = {
  /** The deployed build this tab must move to; `null` while no update is required. */
  required: { target: string } | null
  requireUpdate: (target: string) => void
}

export const useWebUpdateStore = create<WebUpdateState>(set => ({
  required: null,
  // A later mandatory deploy moves the target forward; nothing moves it back to `null`.
  requireUpdate: target => set({ required: { target } }),
}))

/** Non-reactive read for code outside React (the axios interceptor). */
export function isWebUpdateRequired(): boolean {
  return useWebUpdateStore.getState().required !== null
}

export function resetWebUpdateStoreForTests(): void {
  useWebUpdateStore.setState({ required: null })
}
